import { ConvexHttpClient } from "convex/browser";
import { api } from "../../../convex/_generated/api";

const convexUrl = import.meta.env.VITE_CONVEX_URL;
if (!convexUrl) {
  throw new Error("VITE_CONVEX_URL environment variable is required");
}

export const convex = new ConvexHttpClient(convexUrl);

// Derive the HTTP Action URL (.site instead of .cloud)
const httpUrl = convexUrl.replace(/\.convex\.cloud$/, ".convex.site");
const publicApiBaseUrl = import.meta.env.VITE_PUBLIC_API_BASE_URL?.trim();
const apiBaseUrl = publicApiBaseUrl ?? httpUrl;

export function getPublicApiBaseUrl(): string {
  return apiBaseUrl;
}

// ============================================================================
// Auth helpers
// ============================================================================

export function getConvexToken(): string | null {
  try {
    return localStorage.getItem("stalkie_convex_token");
  } catch {
    return null;
  }
}

export function setConvexToken(token: string): void {
  try {
    localStorage.setItem("stalkie_convex_token", token);
  } catch {
    // localStorage unavailable
  }
}

export function clearConvexToken(): void {
  try {
    localStorage.removeItem("stalkie_convex_token");
  } catch {
    // localStorage unavailable
  }
}

export function setAuth(token: string): void {
  setConvexToken(token);
  convex.setAuth(token);
}

export function clearAuth(): void {
  clearConvexToken();
  convex.clearAuth();
}

// Initialize auth from stored token on load
const stored = getConvexToken();
if (stored) {
  convex.setAuth(stored);
}

// ============================================================================
// Generic query/mutation wrappers
// ============================================================================

function ref(name: string) {
  return name.split(":").reduce<unknown>((acc, segment) => {
    if (acc && typeof acc === "object") {
      return (acc as Record<string, unknown>)[segment];
    }
    throw new Error(`Unknown Convex function: ${name}`);
  }, api) as never;
}

export async function query(name: string, args: Record<string, unknown> = {}): Promise<unknown> {
  return (convex.query as (r: unknown, a: unknown) => Promise<unknown>)(ref(name), args);
}

export async function mutation(name: string, args: Record<string, unknown> = {}): Promise<unknown> {
  return (convex.mutation as (r: unknown, a: unknown) => Promise<unknown>)(ref(name), args);
}

export async function action(name: string, args: Record<string, unknown> = {}): Promise<unknown> {
  return (convex.action as (r: unknown, a: unknown) => Promise<unknown>)(ref(name), args);
}

/**
 * Executes a call to an obfuscated public API endpoint.
 * This is the preferred way to call the backend from the web app
 * to avoid exposing internal Convex function names.
 */
export async function fetchApi(path: string, args: Record<string, unknown> = {}): Promise<any> {
  const token = getConvexToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: "POST",
    headers,
    body: JSON.stringify(args),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    // Convex HTTP actions return { error: { code, message, friendlyMessage } }
    const inner = errorBody?.error ?? errorBody;
    const message =
      typeof inner?.message === "string"
        ? inner.message
        : `HTTP error! status: ${response.status}`;
    const error = new Error(message) as Error & {
      data?: Record<string, unknown>;
    };
    error.data = inner;
    throw error;
  }

  return response.json();
}
